"use client";
import React, { useState, useEffect, useRef } from "react";
import { usePathname, useRouter } from "next/navigation";
import { Logo, ThemeToggle, ButtonNav, Select, InputField } from "@/shared";
import { HeaderProps } from "@/interface";
import { useGlobalContext } from "@/contexts/AppContext";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "@/store/configureStore";
import Link from "next/link";
import Image from "next/image";
import styles from "./Header.module.scss";
import Navselect from "../selects/navselect/Navselect";
import useSearchProducts from "@/hooks/products/useSearchProducts";
import { updateActiveProduct } from "@/store/slices/productSlice";
import { formatURL } from "@/utils";
import { useValidateNewUser } from "@/hooks";
import formatNum from "@/utils/formatNum";

const Header = ({ isNavButton = false }: HeaderProps) => {
	const { theme, drinkType } = useGlobalContext();
	const route = usePathname();
	const router = useRouter();
	const user = useSelector((state: AppState) => state.user);
	const location = useSelector((state: AppState) => state.location);
	const capitalizeFirstLetter = (string: string) => {
		return string.charAt(0).toUpperCase() + string.slice(1);
	};
	const cart = useSelector((state: AppState) => state.cart);
	const groupCart = useSelector((state: AppState) => state.group_cart);
	const { handleLogout } = useValidateNewUser();
	const items = groupCart?.items;
	const activeCart = groupCart?.allowShopping;
	const dispatch = useDispatch();
	const quantityInCart = cart?.items?.reduce((a: number, item: any) => {
		const itemQuantity = item?.quantity;
		return a + itemQuantity;
	}, 0);
	const quantityInGroupCart = items?.reduce((a: number, item: any) => {
		const itemQuantity = item?.quantity;
		return a + itemQuantity;
	}, 0);
	const isCategoriesRoute =
		(route.includes("/alcohol") &&
			!route.includes("/alcohol/products") &&
			(!route.includes("/alcohol/categories/") ||
				route === "/alcohol/categories")) ||
		route === "/" ||
		route === "/categories";
	const [isOpen, setIsOpen] = useState<boolean | null>(false);
	const [collapsed, setCollapsed] = useState<boolean>(true);
	const [searchOpen, setSearchOpen] = useState<boolean>(false);

	const toggleDropdown = () => setIsOpen(!isOpen);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const refNode = dropdownRef.current;
	useEffect(() => {
		if (!collapsed) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "auto";
		}

		return () => {
			document.body.style.overflow = "auto";
		};
	}, [collapsed]);
	const defaultOptionIndex = route.includes("/alcohol") ? 1 : 0;

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (refNode && !refNode.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};
		document.addEventListener("click", handleClickOutside);

		return () => {
			document.removeEventListener("click", handleClickOutside);
		};
	}, [refNode]);
	const options = [
		{ label: "Non-Alcoholic", value: "non-alcohol" },
		{ label: "Alcoholic", value: "alcohol" },
	];

	const handleOptionChange = (value: string) => {
		if (value === "alcohol") {
			if (route === "/alcohol") return;
			if (route === "/alcohol/categories") return;
			const newPath = `/alcohol${route === "/" ? "" : route}`;
			router.push(newPath);
		} else {
			router.push(route.replace("/alcohol", "") || "/");
		}
	};

	const [query, setQuery] = useState<string>("");
	const [searchResults, setSearchResults] = useState<any[]>([]);
	const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
	const { fetchSearchProducts } = useSearchProducts();
	const searchRef = useRef<HTMLDivElement>(null);

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setQuery(value);

		if (value.trim() === "") {
			setSearchResults([]);
			setIsDropdownOpen(false);
			return;
		}

		const currentQuery = value;
		fetchSearchProducts(value, (results: any[]) => {
			if (currentQuery === inputRef.current?.value) {
				setSearchResults(results);
			}
		});
	};

	useEffect(() => {
		setIsDropdownOpen(
			query.trim() !== "" && (searchResults.length > 0 || query !== "")
		);
	}, [query, searchResults]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
				setIsDropdownOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (searchOpen && inputRef.current) {
			inputRef.current.focus();
		}
	}, [searchOpen]);

	return (
		<header className={`${styles.header}`} data-type={theme} data-value={drinkType}>
			{!searchOpen ? (
				<>
					<div className={styles.small_row}>
						<Hamburger collapsed={collapsed} setCollapsed={setCollapsed} />
						<Link href="/">
							<div
								className={styles.header_logoContainer}
								onClick={() => setCollapsed(true)}
							>
								<Logo />
							</div>
						</Link>
						<div className={styles.desk_view}>
							<ThemeToggle />
						</div>
					</div>

					<div
						onClick={() => setCollapsed(true)}
						className={
							styles[
								!collapsed
									? "header_wrapper"
									: "header_wrapper__collapsed"
							]
						}
						data-active={!collapsed}
					>
						<div
							onClick={e => e.stopPropagation()}
							className={styles.header_wrapper_body}
						>
							{isNavButton && isCategoriesRoute && (
								<div className={styles.buttons}>
									<ButtonNav />
								</div>
							)}
							<div
								data-theme={theme}
								className={`${styles.mob_view} ${styles.tabs}`}
							>
								<div className={styles.tab_top}>
									<div
										onClick={() => {
											router.push("/");
											setCollapsed(true);
										}}
									>
										<Logo />
									</div>

									<div
										className={styles.filter_icon}
										onClick={() => {
											setCollapsed(true);
										}}
									>
										<Image
											alt=""
											fill
											src={`/svgs/plus-${theme}.svg`}
										/>
									</div>
								</div>
								<div className={styles.profile_data}>
									<p>Navigation</p>
									<div
										className={`${styles.tab_item}`}
										onClick={() => {
											router.push("/");
											setCollapsed(true);
										}}
									>
										<div
											data-theme={theme}
											className={styles.tab_icon}
										>
											<Image
												className={styles.icon_image}
												alt="icon"
												fill
												src="/svgs/homeIcon.svg"
											/>
										</div>
										<h3
											className={
												route === "/" ? styles.active_tab : ""
											}
										>
											Home
										</h3>
										{route === "/" && (
											<div className={styles.icon}>
												<Image
													alt=""
													fill
													src="/svgs/indicator.svg"
												/>
											</div>
										)}
									</div>
									{!user.token && (
										<div
											className={`${styles.tab_item} ${styles.nav_tab_item}`}
											onClick={() => {
												router.push("/login");
												setCollapsed(true);
											}}
										>
											<div
												data-theme={theme}
												className={styles.tab_icon}
											>
												<Image
													className={styles.icon_image}
													alt="icon"
													fill
													src="/svgs/tag-user.svg"
												/>
											</div>
											<h3
												className={
													route === "/login"
														? styles.active_tab
														: ""
												}
											>
												Login
											</h3>
										</div>
									)}

									{!user.token && (
										<div
											className={`${styles.tab_item}`}
											onClick={() => {
												router.push("/create-account");
												setCollapsed(true);
											}}
										>
											<div
												data-theme={theme}
												className={styles.tab_icon}
											>
												<Image
													className={styles.icon_image}
													alt="icon"
													fill
													src="/svgs/user-add.svg"
												/>
											</div>
											<h3
												className={
													route === "/create-account"
														? styles.active_tab
														: ""
												}
											>
												Sign Up
											</h3>
										</div>
									)}
								</div>
								{user.token && (
									<div className={styles.profile_data}>
										<p>My Profile</p>
										<div
											className={styles.tab_item}
											onClick={() => {
												router.push("/account");
												setCollapsed(true);
											}}
										>
											<div className={styles.tab_icon}>
												<Image
													alt="icon"
													fill
													src={`/svgs/basicInfo-${theme}.svg`}
												/>
											</div>
											<h3
												className={
													route === "/account"
														? styles.active_tab
														: ""
												}
											>
												Basic information
											</h3>
											{route === "/account" && (
												<div className={styles.icon}>
													<Image
														alt=""
														fill
														src="/svgs/indicator.svg"
													/>
												</div>
											)}
										</div>
										<div
											className={styles.tab_item}
											onClick={() => {
												router.push("/account/address");
												setCollapsed(true);
											}}
										>
											<div className={styles.tab_icon}>
												<Image
													alt="icon"
													fill
													src={`/svgs/location-${theme}.svg`}
												/>
											</div>
											<h3
												className={
													route === "/account/address"
														? styles.active_tab
														: ""
												}
											>
												My Addresses
											</h3>
											{route === "/account/address" && (
												<div className={styles.icon}>
													<Image
														alt=""
														fill
														src="/svgs/indicator.svg"
													/>
												</div>
											)}
										</div>
										<div
											className={styles.tab_item}
											onClick={() => {
												router.push("/account/favourites");
												setCollapsed(true);
											}}
										>
											<div className={styles.tab_icon}>
												<Image
													alt="icon"
													fill
													src={`/svgs/favourite-${theme}.svg`}
												/>
											</div>
											<h3
												className={
													route === "/account/favourites"
														? styles.active_tab
														: ""
												}
											>
												My favorites
											</h3>
											{route === "/account/favourites" && (
												<div className={styles.icon}>
													<Image
														alt=""
														fill
														src="/svgs/indicator.svg"
													/>
												</div>
											)}
										</div>
									</div>
								)}

								{user.token && (
									<div className={styles.orders_data}>
										<p>My Orders</p>
										<div
											className={styles.tab_item}
											onClick={() => {
												router.push("/account/ongoing-orders");
												setCollapsed(true);
											}}
										>
											<div className={styles.tab_icon}>
												<Image
													alt="icon"
													fill
													src={`/svgs/clipboardIcon-${theme}.svg`}
												/>
											</div>
											<h3
												className={
													route === "/account/ongoing-orders"
														? styles.active_tab
														: ""
												}
											>
												Ongoing Orders
											</h3>
											{route === "/account/ongoing-orders" && (
												<div className={styles.icon}>
													<Image
														alt=""
														fill
														src="/svgs/indicator.svg"
													/>
												</div>
											)}
										</div>
										<div
											className={styles.tab_item}
											onClick={() => {
												router.push("/account/completed-orders");
												setCollapsed(true);
											}}
										>
											<div className={styles.tab_icon}>
												<Image
													alt="icon"
													fill
													src={`/svgs/clipboardTick-${theme}.svg`}
												/>
											</div>
											<h3
												className={
													route === "/account/completed-orders"
														? styles.active_tab
														: ""
												}
											>
												Completed Orders
											</h3>
											{route === "/account/completed-orders" && (
												<div className={styles.icon}>
													<Image
														alt=""
														fill
														src="/svgs/indicator.svg"
													/>
												</div>
											)}
										</div>
										<div
											className={styles.tab_item}
											onClick={() => {
												router.push("/account/cancelled-orders");
												setCollapsed(true);
											}}
										>
											<div className={styles.tab_icon}>
												<Image
													alt="icon"
													fill
													src={`/svgs/clipboardClose-${theme}.svg`}
												/>
											</div>
											<h3
												className={
													route === "/account/cancelled-orders"
														? styles.active_tab
														: ""
												}
											>
												Cancelled Orders
											</h3>
											{route === "/account/cancelled-orders" && (
												<div className={styles.icon}>
													<Image
														alt=""
														fill
														src="/svgs/indicator.svg"
													/>
												</div>
											)}
										</div>
									</div>
								)}

								<div className={styles.orders_data}>
									<p>Location</p>
									<div
										className={styles.tab_item}
										onClick={() => {
											router.push("/location-select");
											setCollapsed(true);
										}}
									>
										<div className={styles.tab_icon}>
											<Image
												alt="icon"
												fill
												src={`/svgs/location-${theme}.svg`}
											/>
										</div>
										<h3>
											{capitalizeFirstLetter(
												location?.activeLocation?.name || ""
											)}
										</h3>
										<div className={styles.icon}>
											<Image
												alt=""
												fill
												src="/svgs/arrow-right-icon.svg"
												className={styles.arrow_icon}
											/>
										</div>
									</div>
								</div>

								{user.token && (
									<div
										onClick={async () => {
											await handleLogout();
											setCollapsed(true);
										}}
										className={styles.tab_item}
									>
										<div className={styles.tab_icon}>
											<Image
												alt="icon"
												fill
												src="/svgs/logout.svg"
											/>
										</div>
										<h4>Log out</h4>
									</div>
								)}

								<div
									className={`${styles.mob_view} ${styles.toggle_container}`}
								>
									<ThemeToggle />
									<p>
										<span>{theme}</span> mode
									</p>
								</div>
							</div>
						</div>
					</div>

					<div
						ref={dropdownRef}
						data-route={route === "/checkout"}
						className={`${styles.small_row} ${styles.nav_buttons}`}
					>
						<div className={styles.mob_view}>
							{isNavButton && isCategoriesRoute && (
								<Navselect
									options={options}
									onOptionChange={handleOptionChange}
									defaultOptionIndex={defaultOptionIndex}
								/>
							)}
						</div>
						<div
							onClick={() => {
								setSearchOpen(!searchOpen);
							}}
							className={styles.button_container}
						>
							<div className={styles.icon}>
								<Image
									src={`/svgs/search-${theme}.svg`}
									alt="icon"
									fill
								/>
							</div>
						</div>
						{user?.id === "" || user?.id === undefined ? (
							<div
								onClick={toggleDropdown}
								className={`${styles.desk_view} ${styles.button_container}`}
							>
								<div className={styles.icon}>
									<Image
										alt="icon"
										fill
										src={`/svgs/User2-${theme}.svg`}
									/>
								</div>
								{isOpen && (
									<div className={styles.dropdown_menu}>
										<div className={styles.dropdown_container}>
											<div
												className={styles.sign_up}
												onClick={() =>
													router.push("/create-account")
												}
											>
												<h3>Sign Up</h3>
											</div>
											<div
												className={styles.sign_up}
												onClick={() => router.push("/login")}
											>
												<h3>Login</h3>
											</div>
											<div
												className={styles.login}
												onClick={() =>
													router.push("/location-select")
												}
											>
												<h3>
													Location(
													{capitalizeFirstLetter(
														location?.activeLocation?.name ||
															""
													)}
													)
												</h3>
											</div>
										</div>
									</div>
								)}
							</div>
						) : (
							<div
								onClick={toggleDropdown}
								className={`${styles.desk_view} ${styles.button_container}`}
							>
								<div className={styles.icon}>
									<Image
										alt="icon"
										fill
										src={`/svgs/User2-${theme}.svg`}
									/>
								</div>
								{isOpen && (
									<div className={styles.dropdown_menu}>
										<div className={styles.dropdown_container}>
											<div
												className={styles.account_option}
												onClick={() => router.push("/account")}
											>
												<h3>My Account</h3>
												<div className={styles.arrow_icon}>
													<Image
														alt="arrow"
														fill
														src="/svgs/arrow-right-icon.svg"
														className={styles.arrow_icon}
													/>
												</div>
											</div>
											<div
												className={styles.account_option}
												onClick={() =>
													router.push("/location-select")
												}
											>
												<h3>
													Location (
													{capitalizeFirstLetter(
														location?.activeLocation?.name ||
															""
													)}
													)
												</h3>
												<div className={styles.arrow_icon}>
													<Image
														alt="arrow"
														fill
														src="/svgs/arrow-right-icon.svg"
														className={styles.arrow_icon}
													/>
												</div>
											</div>
										</div>
									</div>
								)}
							</div>
						)}

						<div className={styles.mob_view}>
							<Link href="/cart">
								<div
									data-type={theme}
									className={styles.button_container}
								>
									<div className={styles.icon}>
										<Image
											alt="cart"
											fill
											src={`/svgs/cart-${theme}.svg`}
										/>
									</div>
									<div
										data-route={route.includes("/alcohol")}
										className={styles.item}
									>
										<p
											data-type={theme}
											className={styles.cart_container_text}
										>
											{activeCart ? (
												<>
													{quantityInGroupCart
														? quantityInGroupCart
														: 0}
												</>
											) : (
												<>{quantityInCart ? quantityInCart : 0}</>
											)}
										</p>
									</div>
								</div>
							</Link>
						</div>
						<div className={styles.desk_view}>
							<Link href="/cart">
								<div data-type={theme} className={styles.cart_button}>
									<div className={styles.cart_icon}>
										<Image
											alt="cart"
											fill
											src={`/svgs/cart-${theme}.svg`}
										/>
									</div>
									{activeCart ? (
										<>
											{groupCart?.items ? (
												<p
													data-type={theme}
													className={styles.cart_container_text}
												>
													₦{formatNum(groupCart.totalPrice)} (
													{quantityInGroupCart
														? quantityInGroupCart
														: 0}
													)
												</p>
											) : (
												<p
													data-type={theme}
													className={styles.cart_container_text}
												>
													{groupCart?.items?.length || 0}{" "}
													{groupCart?.items?.length > 1
														? "items"
														: "item"}
												</p>
											)}
										</>
									) : (
										<>
											{cart.items ? (
												<p
													data-type={theme}
													className={styles.cart_container_text}
												>
													₦{formatNum(cart.totalPrice)} (
													{quantityInCart ? quantityInCart : 0})
												</p>
											) : (
												<p
													data-type={theme}
													className={styles.cart_container_text}
												>
													{cart?.items?.length || 0}{" "}
													{cart?.items?.length > 1
														? "items"
														: "item"}
												</p>
											)}
										</>
									)}
								</div>
							</Link>
						</div>
					</div>
				</>
			) : (
				<>
					<section
						data-type={theme}
						className={styles.search_nav}
						ref={searchRef}
					>
						<div className={styles.search_navLeft}>
							<div className={styles.search_icon}>
								<Image
									className={styles.searchBtn}
									src="/svgs/searchIcon-light.svg"
									alt="searchIcon"
									fill
								/>
							</div>
							<InputField
								ref={inputRef}
								placeholder="Search products or categories"
								value={query}
								onChange={handleSearch}
								wrapperClassName="search_input_wrapper"
								inputClass="search_input_wrapper"
								isNonModal={true}
							/>
						</div>

						<div className={styles.search_navRight}>
							{query && (
								<div
									className={styles.close_icon}
									onClick={() => setQuery("")}
								>
									<Image
										className={styles.searchBtn}
										src="/svgs/nav-close.svg"
										alt="closeIcon"
										fill
									/>
								</div>
							)}

							<p
								onClick={() => {
									setSearchOpen(false);
									setQuery("");
								}}
							>
								Close
							</p>
						</div>

						{isDropdownOpen && (
							<div className={styles.dropdownSearch_menu}>
								<div className={styles.dropdownSearch_container}>
									{searchResults.length > 0 ? (
										searchResults.map(item => (
											<div
												key={item.id}
												className={styles.dropdownSearch_item}
												onClick={() => {
													dispatch(updateActiveProduct(item));
													if (item.type === "non-alcoholic") {
														router.push(
															`/products/${formatURL(
																item?.name
															)}`
														);
														setQuery("");
													} else {
														router.push(
															`/alcohol/products/${formatURL(
																item?.name
															)}`
														);
														setQuery("");
													}
													setSearchOpen(false);
												}}
											>
												<div className={styles.dropdown_image}>
													<Image
														src={item.image}
														alt="image"
														fill
														sizes="(max-width: 300px) 300px, 300px"
													/>
												</div>
												<div className={styles.dropdown_texts}>
													<h3>{item.name}</h3>
													<p>{item.variant}</p>
												</div>
											</div>
										))
									) : (
										<h3 className={styles.noResults}>
											No matching products found
										</h3>
									)}
								</div>
							</div>
						)}
					</section>
				</>
			)}
		</header>
	);
};

export default Header;

function Hamburger({ collapsed, setCollapsed }: any) {
	const handleToggleClick = () => {
		setCollapsed(!collapsed);
	};
	return (
		<div className={styles.controls}>
			<button
				aria-hidden="true"
				onClick={handleToggleClick}
				aria-pressed={!collapsed}
			>
				<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none">
					<rect width="18" height="1.5" fill="red" ry="0.75" x="3" y="6.25" />
					<rect width="18" height="1.5" fill="red" ry="0.75" x="3" y="11.25" />
					<rect width="18" height="1.5" fill="red" ry="0.75" x="3" y="16.25" />
				</svg>
			</button>
		</div>
	);
}
